import React, {useState, useEffect} from "react";
import { authService } from '../../services/auth.service';
import { useUser } from '../../contexts/userContext';
import { stripeService } from '../../services/stripe.service';
import { Link, useNavigate } from "react-router-dom";
import { getAuth, signOut } from 'firebase/auth';
import { firebaseApp } from '../../firebase-config';
import { extractErrorMessage } from '../../helpers/errorHandler';
import whiteLogo from '../../assets/icons/Logo_white.svg';
import closeIcon from '../../assets/icons/close.svg';
import UserInfo from '../../components/UserInfo/UserInfo';
import toast, { Toaster } from 'react-hot-toast';
import '../../Styles/Contact.scss';

const SubscriptionPage = () => {
  const authentication = getAuth(firebaseApp);
  const navigate = useNavigate();

  const [subscriptionInfo, setSubscriptionInfo] = useState<any>();
  const { state: user } = useUser();
  const { state: {isCancelled, feedback, trial}, dispatch} = useUser();
  const [isApple, setIsApple] = useState(false);
  const [isResellerSubscription, setIsResellerSubscription] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const getSubscriptionInfo = async () => {
      const response = await authService.getSubscriptionInfo();
      if(response) {
        setSubscriptionInfo(response);
        if(response.responseObject.stripeCardPlaceHolder === '') {
          setIsApple(true);        
        }
        if(response.responseObject.isResellerSubscription) {
          setIsResellerSubscription(true);
        }
      }
    }
    getSubscriptionInfo();
  }, [])

  const handleCancelClick = () => {
    setShowCancelModal(true);
    setError(null);
  };

  const handleConfirmCancel = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const serverResult = await stripeService.cancelSubscription(user.subscriptionId);

      if (serverResult.responseObject && serverResult.responseObject.status === 'active') {
        dispatch({type: 'set_request_cancelled', payload: true});
        
        const updatedInfo = await authService.getSubscriptionInfo();
        if (updatedInfo) {
          setSubscriptionInfo(updatedInfo);
        }
        
        setShowCancelModal(false);
      } else {
        setError('Failed to cancel subscription. Please try again or contact support.');
      }
    } catch (err) {
      setError('An error occurred while canceling subscription. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!isLoading) {
      setShowCancelModal(false);
      setError(null);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setShowDeleteModal(false);
      setDeleteError(null);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await authService.deleteAccount();

      if (response.isSuccess) {
        toast.success('Your account has been successfully deleted.');

        await signOut(authentication);

        localStorage.clear();
        sessionStorage.clear();

        dispatch({ type: 'logout' });

        navigate('/');
      } else {
        const errorMsg =
          response.errors && response.errors.length > 0
            ? response.errors.join('. ')
            : 'Failed to delete account. Please try again.';
        setDeleteError(errorMsg);
      }
    } catch (err: any) {
      const status = err?.response?.status;

      if (status === 400) {
        const errorMsg = extractErrorMessage(err, 'Bad request. Please try again.');
        setDeleteError(errorMsg);
      } else if (status >= 500) {
        setDeleteError(
          'Account deletion completed with some issues. Please contact support if you experience problems.'
        );
        console.error('Delete account server error:', err);
      } else {
        const errorMsg = extractErrorMessage(err, 'An unexpected error occurred. Please try again.');
        setDeleteError(errorMsg);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <UserInfo>
      {
        !subscriptionInfo ? (
          <h2>Loading...</h2>
        ) : (
          <div className="subscription__page">
            <div className="subscription__info">
              {
                trial ? (
                  <h2>Free trial</h2>
                ) : (
                  <h2>Subscription</h2>
                )
              }
              <p>{trial ? 'Trial start date:' : 'Start date:'} <span className="date-info">{subscriptionInfo?.responseObject.startDate.split('T')[0]}</span></p>
              <p>{trial ? 'Trial end date:' : 'End date:'} <span className="date-info">{subscriptionInfo?.responseObject.renewDate.split('T')[0]}</span></p>
              {
                trial ? (
                  <p>Would you like to leave us your feedback? Click <Link to='/feedback'><span className="text-underline">here</span></Link></p> 
                ) : null
              }
              {isCancelled ? (
                <div className="subscription-cancelled">
                  <p>Your subscription is cancelled, and will not be renewed at end date. You will still have access to TruHu until end date.</p><br></br>
                  {
                    !feedback ? (
                      <p>Would you like to leave us your feedback? Click <Link to='/feedback'><span className="text-underline">here</span></Link></p> 
                    ) : null
                  }
                </div>
              ) : (
                (!trial || !isApple) && !isResellerSubscription ? (
                  <p
                    onClick={handleCancelClick}
                    className="cursor-pointer cancel-subscription"
                  >
                      Cancel subscription
                  </p>
                ) : (
                  null
                )
              )}
              <p
                onClick={handleDeleteClick}
                className="cursor-pointer delete-account-link"
              >
                Delete Account
              </p>

            </div>
            {
              (!trial || !isApple) && !isResellerSubscription ? (
                <div className="subscription__card-info">
                  <h2>Card</h2>
                  <div className="subscription__card">
                    <img src={whiteLogo} alt="" className="card-logo"/>
                    <p>**** **** **** {subscriptionInfo?.responseObject.stripeCardPlaceHolder}</p>
                    <p>{subscriptionInfo?.responseObject.stripeCardName}</p>
                  </div>
                  <a href={subscriptionInfo?.responseObject.stripePortalUrl}>
                  <button className="subscription__button">Manage card info</button>
                  </a>
                </div>
              ) : null
            }
          </div>
        )
      }

      {showCancelModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={handleCloseModal}
              disabled={isLoading}
            >
              <img src={closeIcon} alt="Close" />
            </button>
            
            <h2 className="modal-title">Cancel Subscription</h2>
            <p className="modal-text">
              Are you sure you want to cancel your subscription? You will still have access to TruHu until the end of your billing period.
            </p>

            {error && (
              <div className="modal-error">
                <p>{error}</p>
              </div>
            )}

            <div className="modal-buttons">
              <button
                className="modal-button modal-button-secondary"
                onClick={handleCloseModal}
                disabled={isLoading}
              >
                Keep Subscription
              </button>
              <button
                className="modal-button modal-button-danger"
                onClick={handleConfirmCancel}
                disabled={isLoading}
              >
                {isLoading ? 'Canceling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={handleCloseDeleteModal}
              disabled={isDeleting}
            >
              <img src={closeIcon} alt="Close" />
            </button>

            <h2 className="modal-title">Are you sure you want to delete your account?</h2>
            <p className="modal-text">
              This action is permanent and cannot be undone. All your data, subscriptions, and
              calibration history will be permanently deleted.
            </p>

            {deleteError && (
              <div className="modal-error">
                <p>{deleteError}</p>
              </div>
            )}

            <div className="modal-buttons">
              <button
                className="modal-button modal-button-secondary"
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="modal-button modal-button-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </UserInfo>
  );
}

export default SubscriptionPage;